import {
  JobsApi,
  BackupsApi,
  ListBackupJobsTypesApi,
  ListRestoreJobsTypesApi,
  ListScanJobsTypesApi,
  ListBackupJobErrorCodesApi,
  ListRestoreJobErrorCodesApi,
  ListScanJobErrorCodesApi,
  JobStatus,
} from '@repo/api-gw-sdk';
import type {
  Condition,
  Configuration,
  TakeNativeSnapshotRequestVaultProtectionModeEnum,
} from '@repo/api-gw-sdk';
import type { ColumnSort } from '@tanstack/react-table';

import { toJsonText } from '@/components/queryBuilder/QueryLanguageConverter';

import type { HttpClient } from './httpClient';

const baseUrl = '/jobs';

const JOB_DETAIL_REFRESH_INTERVAL_MS = 15_000;
const JOB_LIST_REFRESH_INTERVAL_MS = 30_000;
const JOB_STEPS_REFRESH_INTERVAL_MS = 10_000;

const ACTIVE_JOB_STATUSES = new Set([
  JobStatus.JOB_RUNNING,
  JobStatus.JOB_PENDING,
  JobStatus.JOB_UNSPECIFIED,
]);

const isActiveJobStatus = (status: JobStatus | undefined) =>
  !status || ACTIVE_JOB_STATUSES.has(status);

export const jobs = (
  httpClient: HttpClient,
  config: Configuration,
  currentProjectId: string
) => {
  const jobsApi = new JobsApi(config);
  const backupsApi = new BackupsApi(config);
  const listBackupJobTypesApi = new ListBackupJobsTypesApi(config);
  const listScanJobTypesApi = new ListScanJobsTypesApi(config);
  const listRestoreJobTypesApi = new ListRestoreJobsTypesApi(config);
  const listBackupJobErrorCodesApi = new ListBackupJobErrorCodesApi(config);
  const listRestoreJobErrorCodesApi = new ListRestoreJobErrorCodesApi(config);
  const listScanJobErrorCodesApi = new ListScanJobErrorCodesApi(config);
  return {
    downloadBackupJobs: (columnFilters?: Condition, columns?: string) =>
      jobsApi.backupJobsDownload(
        currentProjectId,
        columnFilters ? toJsonText(columnFilters) : undefined,
        undefined,
        columns
      ),
    downloadRestoreJobs: (columnFilters?: Condition, columns?: string) =>
      jobsApi.restoreJobsDownload(
        currentProjectId,
        columnFilters ? toJsonText(columnFilters) : undefined,
        undefined,
        columns
      ),
    downloadScanJobs: (columnFilters?: Condition, columns?: string) =>
      jobsApi.scanJobsDownload(
        currentProjectId,
        columnFilters ? toJsonText(columnFilters) : undefined,
        undefined,
        columns
      ),
    scan: {
      useJob: (
        jobId: string,
        swrOptions?: {
          fallbackData?: Awaited<ReturnType<typeof jobsApi.getScanJob>>;
        }
      ) =>
        httpClient.executeWithConfig(
          jobsApi.getScanJob.bind(jobsApi),
          jobId ? baseUrl : null,
          '/scan',
          {
            refreshInterval: (data) =>
              isActiveJobStatus(data?.job?.jobExecutionDetails.status)
                ? JOB_DETAIL_REFRESH_INTERVAL_MS
                : 0,
            ...swrOptions,
          },
          currentProjectId,
          jobId
        ),
      useList: (
        pageIndex: number,
        pageSize: number,
        sorting: ColumnSort | undefined,
        columnFilters: Condition | undefined
      ) =>
        httpClient.executeWithConfig(
          jobsApi.listScanJobsInternal.bind(jobsApi),
          baseUrl,
          '/scan',
          { refreshInterval: JOB_LIST_REFRESH_INTERVAL_MS },
          currentProjectId,
          pageIndex,
          pageSize,
          sorting
            ? `${sorting.id}:${sorting.desc ? 'desc' : 'asc'}`
            : undefined,
          columnFilters ? toJsonText(columnFilters) : undefined
        ),
      useListTypes: () =>
        httpClient.execute(
          listScanJobTypesApi.listScanJobsTypes.bind(listScanJobTypesApi),
          baseUrl,
          '/list-scan-job-types',
          currentProjectId
        ),
      useListErrorCodes: () =>
        httpClient.execute(
          listScanJobErrorCodesApi.listScanJobErrorCodes.bind(
            listScanJobErrorCodesApi
          ),
          baseUrl,
          '/list-scan-job-error-codes',
          currentProjectId
        ),
    },
    backup: {
      cancel: (jobId: string) => {
        return jobsApi.cancelBackupJob(currentProjectId, jobId);
      },
      create: (
        resourceId: string,
        vaultId: string,
        retentionDays: number,
        isPitrOnDemand = false
      ) => {
        return backupsApi.takeSnapshot(currentProjectId, resourceId, {
          retentionDays,
          vaultId,
          isPitrOnDemand,
        });
      },
      createNative: (
        resourceId: string,
        retentionDays: number,
        targetRegion?: string,
        vaultProtectionMode?: TakeNativeSnapshotRequestVaultProtectionModeEnum
      ) => {
        return backupsApi.takeNativeSnapshot(currentProjectId, resourceId, {
          retentionDays,
          targetRegion,
          vaultProtectionMode,
        });
      },
      useJob: (
        jobId: string,
        swrOptions?: {
          fallbackData?: Awaited<ReturnType<typeof jobsApi.getBackupJob>>;
        }
      ) =>
        httpClient.executeWithConfig(
          jobsApi.getBackupJob.bind(jobsApi),
          jobId ? baseUrl : null,
          '/backup',
          {
            refreshInterval: (data) =>
              isActiveJobStatus(data?.job?.jobExecutionDetails.status)
                ? JOB_DETAIL_REFRESH_INTERVAL_MS
                : 0,
            ...swrOptions,
          },
          currentProjectId,
          jobId
        ),
      useList: (
        pageIndex: number,
        pageSize: number,
        sorting: ColumnSort | undefined,
        columnFilters: Condition | undefined
      ) =>
        httpClient.executeWithConfig(
          jobsApi.listBackupJobsInternal.bind(jobsApi),
          baseUrl,
          '/backup',
          { refreshInterval: JOB_LIST_REFRESH_INTERVAL_MS },
          currentProjectId,
          pageIndex,
          pageSize,
          sorting
            ? `${sorting.id}:${sorting.desc ? 'desc' : 'asc'}`
            : undefined,
          columnFilters ? toJsonText(columnFilters) : undefined
        ),
      useListTypes: () =>
        httpClient.execute(
          listBackupJobTypesApi.listBackupJobsTypes.bind(listBackupJobTypesApi),
          baseUrl,
          '/list-backup-job-types',
          currentProjectId
        ),
      useListErrorCodes: () =>
        httpClient.execute(
          listBackupJobErrorCodesApi.listBackupJobErrorCodes.bind(
            listBackupJobErrorCodesApi
          ),
          baseUrl,
          '/list-backup-job-error-codes',
          currentProjectId
        ),
      useJobProgressSteps: (jobId: string, isJobActive: boolean) =>
        httpClient.executeWithConfig(
          jobsApi.getBackupJobProgressSteps.bind(jobsApi),
          jobId ? baseUrl : null,
          '/backup/progress-steps',
          { refreshInterval: isJobActive ? JOB_STEPS_REFRESH_INTERVAL_MS : 0 },
          currentProjectId,
          jobId
        ),
    },
    restore: {
      cancel: (jobId: string) => {
        return jobsApi.cancelRestoreJob(currentProjectId, jobId);
      },
      useJob: (
        jobId: string,
        swrOptions?: {
          fallbackData?: Awaited<ReturnType<typeof jobsApi.getRestoreJob>>;
        }
      ) =>
        httpClient.executeWithConfig(
          jobsApi.getRestoreJob.bind(jobsApi),
          jobId ? baseUrl : null,
          '/restore',
          {
            refreshInterval: (data) =>
              isActiveJobStatus(data?.job?.jobExecutionDetails.status)
                ? JOB_DETAIL_REFRESH_INTERVAL_MS
                : 0,
            ...swrOptions,
          },
          currentProjectId,
          jobId
        ),
      useList: (
        pageIndex: number,
        pageSize: number,
        sorting: ColumnSort | undefined,
        columnFilters: Condition | undefined
      ) =>
        httpClient.executeWithConfig(
          jobsApi.listRestoreJobsInternal.bind(jobsApi),
          baseUrl,
          '/restore',
          { refreshInterval: JOB_LIST_REFRESH_INTERVAL_MS },
          currentProjectId,
          pageIndex,
          pageSize,
          sorting
            ? `${sorting.id}:${sorting.desc ? 'desc' : 'asc'}`
            : undefined,
          columnFilters ? toJsonText(columnFilters) : undefined
        ),
      useListTypes: () =>
        httpClient.execute(
          listRestoreJobTypesApi.listRestoreJobsTypes.bind(
            listRestoreJobTypesApi
          ),
          baseUrl,
          '/list-restore-job-types',
          currentProjectId
        ),
      useListErrorCodes: () =>
        httpClient.execute(
          listRestoreJobErrorCodesApi.listRestoreJobErrorCodes.bind(
            listRestoreJobErrorCodesApi
          ),
          baseUrl,
          '/list-restore-job-error-codes',
          currentProjectId
        ),
      useJobProgressSteps: (jobId: string, isJobActive: boolean) =>
        httpClient.executeWithConfig(
          jobsApi.getRestoreJobProgressSteps.bind(jobsApi),
          jobId ? baseUrl : null,
          '/restore/progress-steps',
          { refreshInterval: isJobActive ? JOB_STEPS_REFRESH_INTERVAL_MS : 0 },
          currentProjectId,
          jobId
        ),
    },
  };
};
